import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Rating,
  useTheme,
  alpha,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import WeChatQRDialog from '../common/WeChatQRDialog';
import useWeChatQR from '../../hooks/useWeChatQR';

const FeaturedUniversities = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { openQR, openWeChatQR, closeWeChatQR } = useWeChatQR();
  const universities = [
    {
      id: 1,
      name: '马来亚大学',
      nameEn: 'University of Malaya',
      location: '吉隆坡',
      ranking: 'QS世界排名 #70',
      students: '27,000+',
      rating: 4.8,
      image: '/images/universities/um.jpg',
      specialties: ['医学', '工程', '法学', '商学'],
      description: '马来西亚历史最悠久、声誉最高的大学之一，在医学和工程领域享有盛誉。',
      tuitionRange: 'RM 15,000 - 45,000/年',
      detailUrl: '/universities/university-of-malaya',
    },
    {
      id: 2,
      name: '博特拉大学',
      nameEn: 'Universiti Putra Malaysia',
      location: '雪兰莪',
      ranking: 'QS世界排名 #143',
      students: '30,000+',
      rating: 4.7,
      image: '/images/universities/upm.jpg',
      specialties: ['农业', '环境科学', '生物技术', '工程'],
      description: '在农业、生物技术和环境科学领域处于亚洲领先地位的研究型大学。',
      tuitionRange: 'RM 12,000 - 38,000/年',
      detailUrl: '/universities/universiti-putra-malaysia',
    },
    {
      id: 3,
      name: '马来西亚国民大学',
      nameEn: 'Universiti Kebangsaan Malaysia',
      location: '雪兰莪',
      ranking: 'QS世界排名 #129',
      students: '26,000+',
      rating: 4.6,
      image: '/images/universities/ukm.jpg',
      specialties: ['社会科学', '教育学', '医学', '工程'],
      description: '马来西亚重点研究型大学，在社会科学和教育学领域有杰出表现。',
      tuitionRange: 'RM 14,000 - 42,000/年',
      detailUrl: '/universities/the-national-university-of-malaysia',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box sx={{ py: 8, backgroundColor: theme.palette.background.default }}>
      <Container maxWidth="xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: theme.palette.text.primary,
              }}
            >
              热门推荐院校
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 600,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.2rem' },
              }}
            >
              精选马来西亚顶尖大学，为您提供世界一流的教育体验
            </Typography>
          </Box>

          {/* Universities Grid */}
          <Grid container spacing={4}>
            {universities.map((university, index) => (
              <Grid item xs={12} md={6} lg={4} key={university.id}>
                <motion.div variants={cardVariants}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                    }}
                  >
                    {/* University Image */}
                    <CardMedia
                      component="div"
                      sx={{
                        height: 200,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.8)}, ${alpha(theme.palette.secondary.main, 0.8)})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          textAlign: 'center',
                          textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        }}
                      >
                        {university.name.charAt(0)}
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          backgroundColor: alpha(theme.palette.warning.main, 0.9),
                          color: 'white',
                          px: 2,
                          py: 0.5,
                          borderRadius: 20,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {university.ranking}
                      </Box>
                    </CardMedia>

                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      {/* University Name */}
                      <Typography
                        variant="h5"
                        sx={{
                          mb: 0.5,
                          fontWeight: 700,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {university.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 2,
                          color: theme.palette.text.secondary,
                          fontStyle: 'italic',
                        }}
                      >
                        {university.nameEn}
                      </Typography>

                      {/* Quick Info */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationIcon sx={{ fontSize: 16, mr: 0.5, color: theme.palette.text.secondary }} />
                          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            {university.location}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PeopleIcon sx={{ fontSize: 16, mr: 0.5, color: theme.palette.text.secondary }} />
                          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            {university.students}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Rating */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Rating
                          value={university.rating}
                          readOnly
                          precision={0.1}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {university.rating}/5.0
                        </Typography>
                      </Box>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 2,
                          color: theme.palette.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {university.description}
                      </Typography>

                      {/* Specialties */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                          热门专业：
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {university.specialties.map((specialty) => (
                            <Chip
                              key={specialty}
                              label={specialty}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '0.75rem',
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                color: theme.palette.primary.main,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* Tuition */}
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 3,
                          color: theme.palette.success.main,
                          fontWeight: 600,
                        }}
                      >
                        学费范围：{university.tuitionRange}
                      </Typography>

                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ flexGrow: 1 }}
                          onClick={() => navigate(university.detailUrl)}
                        >
                          查看详情
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={openWeChatQR}
                          sx={{ flexGrow: 1 }}
                        >
                          立即咨询
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* View All Button */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/universities')}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 25,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              查看所有院校
            </Button>
          </Box>
        </motion.div>
      </Container>
      <WeChatQRDialog 
        open={openQR} 
        onClose={closeWeChatQR}
        title="立即咨询"
      />
    </Box>
  );
};

export default FeaturedUniversities;
